import * as Sentry from '@sentry/tanstackstart-react';
import { StartClient } from '@tanstack/react-start/client';
import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { clientEnv } from '@/env';

import { getRouter } from './router';

// PostHog is initialized in RootComponent via initPostHogFromRouteContext,
// which reads bootstrap data from TanStack Start's route context (server function → beforeLoad).

const router = getRouter();

Sentry.init({
  // `envString` is `development` for BOTH a local dev server and the deployed
  // dev.listening.com, so the environment tag can't tell them apart. `localDev`
  // is set only by the dev scripts, and an undefined DSN disables Sentry — so
  // laptop stack traces stay out while dev.listening.com keeps reporting under
  // `development-client`.
  dsn: clientEnv.localDev ? undefined : clientEnv.sentryDsn,
  // Attaches user IP, headers, cookies and query params to events:
  // https://docs.sentry.io/platforms/javascript/guides/tanstackstart-react/configuration/options/#dataCollection
  dataCollection: {
    userInfo: true,
    cookies: true,
    httpHeaders: { request: true, response: true },
    urlQueryParams: true,
  },
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration(),
    // Drop errors whose stack frames are EXCLUSIVELY from third-party CDN
    // scripts we can't fix (e.g. Taboola's tfa.js "Cannot read properties of
    // null (reading 'tagName')" — it crashes in its own global error handler
    // when its pixel is blocked by an ad blocker / GFW). Errors that touch any
    // of our own code are never dropped, so this can't mask our bugs. Relies on
    // the `applicationKey` the Sentry Vite plugin stamps into our bundles
    // (vite.config.ts) — only present in deployed builds, so gate on PROD to
    // avoid dropping genuine errors in dev where no key is injected.
    // oxlint-disable-next-line env-access/no-raw-env-access -- need the literal import.meta.env.PROD so Vite statically strips this in dev
    ...(import.meta.env.PROD
      ? [
          Sentry.thirdPartyErrorFilterIntegration({
            filterKeys: ['listening-web'],
            behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
          }),
        ]
      : []),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production.
  // Learn more at https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error.
  // Learn more at https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  environment: `${clientEnv.envString}-client`,
  // Filter errors from third-party CDN scripts we don't control
  denyUrls: [
    /extensions\//i, // Browser extensions
    /^chrome:\/\//i, // Chrome internal
    /^chrome-extension:\/\//i, // Chrome extensions
    /^moz-extension:\/\//i, // Firefox extensions
  ],
});

// Dev-only render/inspection tooling, on by default in dev and off in
// Playwright (navigator.webdriver), whose screenshots it would cover. The static
// import.meta.env.DEV guard lets Vite dead-code-eliminate the dynamic imports
// (and the packages themselves) from production builds.
// oxlint-disable-next-line env-access/no-raw-env-access -- need the literal import.meta.env.DEV so Vite statically strips these dev tools from prod
if (import.meta.env.DEV && !navigator.webdriver) {
  // dynamic-import -- the DEV guard lets Vite drop react-scan from production builds.
  void import('react-scan').then(({ scan }) => scan({ enabled: true }));
  // dynamic-import -- the DEV guard lets Vite drop react-grab from production builds.
  void import('react-grab');
}

hydrateRoot(
  document,
  <StrictMode>
    <StartClient />
  </StrictMode>,
);
